import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useForm, z, zodResolver } from "@/hooks/schema";
import { Form } from "@/components/Form";
import { Check, CirclePlus, ImageUp, Trash } from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { registerProductGroupsData } from "@/Api/productGroups";
import { useToastNavigate } from "@/utils/toastNavigate";
import { LoaderSpin } from "@/components/Loaders";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";

const schema = z.object({
  name: z.string({ message: "*requerido" }).min(1, "*requerido"),
  image: z.any(),
  description: z.string().optional(),
  color: z.string({ message: "*requerido" }).min(1, "*requerido"),
});

type SchemaType = z.infer<typeof schema>;

export const AddBtn = ({ updateFront }) => {
  const [isOpen, setIsOpen] = useState(false);

  const form = useForm<SchemaType>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      image: null,
      description: "",
      color: "#000000",
    },
  });

  const { toast } = useToastNavigate();
  const [formState, setFormState] = useState<string>("default");

  const formSubmit = async (data: SchemaType) => {
    setFormState("loading");
    const payload = {
      ...data,
      image: data?.image?.[0] || null,
    };

    const { data: apiResponse, ok } = await registerProductGroupsData(payload);
    if (!ok) {
      setFormState("error");

      setTimeout(() => {
        setFormState("default");
      }, 1000);
      setIsOpen(false);
      return;
    }
    const payloadResponse = {
      ...apiResponse?.response,
      is_active: true,
    };

    updateFront((prev) => {
      if (prev?.data.length < 15)
        return {
          ...prev,
          data: [payloadResponse, ...prev.data],
        };

      prev?.data.pop();
      return {
        ...prev,
        data: [payloadResponse, ...prev.data],
        last_page: prev.last_page + 1,
      };
    });

    setFormState("default");
    setIsOpen(false);
    toast({
      description: apiResponse?.message,
      action: <Check color="#1edc2b" />,
    });
  };

  const formWatch = form.watch();

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger>
        <Button
          variant="outline"
          className={cn("py-3 flex gap-1", `bg-primary`)}
        >
          <CirclePlus className="size-5" strokeWidth={1.75} />
          Adicionar Loterias
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[450px] h-[90vh]">
        <DialogHeader className="flex flex-col gap-2">
          <DialogDescription className="text-lg">
            Adicionar Loterias
          </DialogDescription>
          <DialogTitle className="text-2xl">Informações Básicas</DialogTitle>
        </DialogHeader>
        <ScrollArea className="h-full">
          <Form
            onSubmit={form.handleSubmit(formSubmit)}
            {...form}
            className="space-y-3"
          >
            <Form.Row>
              <Form.Field name="name">
                {({ field }) => (
                  <Form.Item>
                    <Form.Label>
                      Nome <Form.Message />
                    </Form.Label>
                    <Form.Control>
                      <Input {...field} placeholder="Insira um nome" />
                    </Form.Control>
                  </Form.Item>
                )}
              </Form.Field>
            </Form.Row>

            <Form.Row>
              <Form.Field name="description">
                {({ field }) => (
                  <Form.Item>
                    <Form.Label>Descrição</Form.Label>
                    <Form.Control>
                      <Input {...field} placeholder="Insira uma descrição" />
                    </Form.Control>
                  </Form.Item>
                )}
              </Form.Field>
            </Form.Row>

            <Form.Row>
              <Form.Field name="color">
                {({ field }) => (
                  <Form.Item>
                    <Form.Label>
                      Cor <Form.Message />
                    </Form.Label>
                    <Form.Control>
                      <div className="flex gap-2 items-center">
                        <Input 
                          {...field} 
                          type="color" 
                          className="w-16 h-10 p-1 rounded cursor-pointer"
                        />
                        <Input 
                          {...field} 
                          placeholder="#000000" 
                          className="flex-1"
                        />
                      </div>
                    </Form.Control>
                  </Form.Item>
                )}
              </Form.Field>
            </Form.Row>

            <div className="mt-5">
              <label className="text-1xl font-medium text-gray-900 dark:text-zinc-300">
                Foto:
              </label>
              <div className="flex justify-center itens-center rounded-md border border-dashed dark:border-gray-500 border-gray-900/25 px-2 py-3 mt-2">
                <div className="flex flex-col items-center gap-4">
                  {formWatch?.image?.length > 0 ? (
                    <img
                      className="rounded-md aspect-square w-40 object-cover"
                      src={URL.createObjectURL(formWatch?.image?.[0])}
                      alt="image_logo"
                    />
                  ) : (
                    <ImageUp className="mx-auto size-12 text-gray-300 mt-2" />
                  )}
                  <div className="flex justify-center items-center gap-5 text-sm/6 text-gray-600">
                    <label
                      htmlFor="file-upload"
                      className="relative cursor-pointer rounded-md bg-transparent font-semibold text-blue-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-blue-600 focus-within:ring-offset-2 hover:text-blue-700"
                    >
                      <span className="flex items-center gap-1">
                        {formWatch?.image?.length > 0 && (
                          <ImageUp className="size-5" />
                        )}
                        Procurar imagem
                      </span>
                      <input
                        id="file-upload"
                        type="file"
                        className="sr-only"
                        accept="image/*"
                        {...form.register("image")}
                      />
                    </label>
                    {formWatch?.image && formWatch?.image?.length > 0 && (
                      <Button
                        className="h-6 px-0 bg-transparent text-slate font-medium hover:text-red-600 hover:bg-transparent flex gap-1"
                        onClick={() => form.setValue("image", null)}
                      >
                        <Trash className="size-5" />
                        Remover
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button
                type="submit"
                className={cn("w-full", colors.bg_primary)}
                disabled={formState !== "default"}
              >
                {formState === "loading" && (
                  <>
                    Adicionando <LoaderSpin />
                  </>
                )}
                {formState === "default" && "Adicionar"}
                {formState === "error" && "Erro ao adicionar"}
              </Button>
            </DialogFooter>
          </Form>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
